import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import useAppSelector from '../../app/redux/useAppSelector';
import type { ButtonProps } from '../../components/Button';
import Button from '../../components/Button';
import { Cards } from '../../components/Card';
import ExternalBrandLogo from '../../components/ExternalBrandLogo';
import SVGIcon from '../../components/SVGIcon';
import Tooltip from '../../components/Tooltip';
import attachmentIcon from '../../icons/attachment.svg';
import SettingCard from '../../settings/SettingCard';
import SettingsContainerView from '../../settings/SettingsContainerView';
import SettingsSection from '../../settings/SettingsSection';
import UIActions from '../../ui/UIActions';
import useNavigate from '../../utils/router/useNavigate';
import { Link } from '../../utils/router/v3Bridge';
import DropLine from '../DropLine';
import useIntegrationMap from '../useIntegrationMap';
import ConfluenceImportModal from './ConfluenceImportModal';
import GithubWikiImportModal from './GithubWikiImportModal';
import GuruImportModal from './GuruImportModal';
import handleImport from './handleImport';
import ImportFileModal from './ImportFileModal';
import MassExportModal from './MassExportModal';
import NotionImportModal from './NotionImportModal';

const PLATFORM_MODALS = {
  confluence: ConfluenceImportModal,
  'github-wiki': GithubWikiImportModal,
  markdown: ImportFileModal,
  notion: NotionImportModal,
  guru: GuruImportModal,
} as const;

const ImportButton = (props: ButtonProps) => (
  <Button variant="secondary" {...props}>
    Import
  </Button>
);

const AdminImportExportView = () => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const isVerified = useAppSelector(
    (state) => state.session.organization.isVerified,
  );

  const [integrations, { loading }] = useIntegrationMap();

  useEffect(() => {
    if (params.status === 'success') {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      navigate(`/admin/import/${params.platform}`, { replace: true });
    } else if (params.status === 'failed') {
      navigate('/admin/import', { replace: true });
    }
  }, [params.platform, params.status, navigate]);

  const showModal = useCallback(
    (platform: keyof typeof PLATFORM_MODALS) => {
      dispatch(
        UIActions.openModal(PLATFORM_MODALS[platform], {
          disableOutsideClose: true,
        }),
      );
      navigate(`/admin/import/${platform}`);
    },
    [dispatch, navigate],
  );

  useEffect(() => {
    if (!loading && params.platform) {
      showModal(params.platform as keyof typeof PLATFORM_MODALS);
    }
  }, [loading, params.platform, showModal]);

  const exportButton = (
    <Button
      onClick={() => {
        dispatch(UIActions.openModal(MassExportModal));
      }}
      variant="secondary"
      disabled={!isVerified}
    >
      Download
    </Button>
  );

  return (
    <SettingsContainerView
      title="Import and Export"
      subtitle="Transfer knowledge to Slab from other tools you use."
    >
      <Cards>
        <SettingCard
          description="Confluence"
          icon={<ExternalBrandLogo brand="atlassian" product="confluence" />}
          actions={<ImportButton onClick={() => showModal('confluence')} />}
          details="Import Confluence Pages."
        />
        <SettingCard
          description="GitHub Wiki"
          icon={<ExternalBrandLogo brand="github" />}
          actions={
            <ImportButton
              onClick={() => {
                const { github: integration } = integrations;
                if (integration) {
                  showModal('github-wiki');
                } else {
                  handleImport('github');
                }
              }}
            />
          }
          details={
            <>
              Import content from GitHub Wiki. You can also continuously{' '}
              <Link to="/admin/integrations/github">sync</Link> markdown files
              from your GitHub repos.
            </>
          }
        />
        <SettingCard
          description="Notion"
          icon={<ExternalBrandLogo brand="notion" />}
          actions={<ImportButton onClick={() => showModal('notion')} />}
          details="Import exported Notion content."
        />
        <SettingCard
          description="Guru"
          icon={<ExternalBrandLogo brand="guru" height={24} />}
          actions={<ImportButton onClick={() => showModal('guru')} />}
          details="Import exported Guru content."
        />
        <SettingCard
          description="Files"
          icon={<SVGIcon size={24}>{attachmentIcon}</SVGIcon>}
          actions={<ImportButton onClick={() => showModal('markdown')} />}
          details="Upload and import HTML and Markdown files."
        />
      </Cards>
      <DropLine message="Want to import content from another source?" />
      <SettingsSection header="Export">
        <Cards>
          <SettingCard
            actions={
              isVerified ? (
                exportButton
              ) : (
                <Tooltip
                  trigger="hover"
                  tooltip={
                    'Exporting is disabled until\nyour email has been verified'
                  }
                  placement="top"
                >
                  {exportButton}
                </Tooltip>
              )
            }
            title="Published posts"
            details="Export published posts as Markdown or Word files."
          />
        </Cards>
      </SettingsSection>
    </SettingsContainerView>
  );
};

export default AdminImportExportView;
